import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { useProgramContext } from '../contexts/ProgramContext';

interface GlobalConfigInfo {
  admins: string[];
  adminCount: number;
  isInitialized: boolean;
}

const GlobalConfig: React.FC = () => {
  const { publicKey } = useWallet();
  const { program } = useProgramContext();
  const [loading, setLoading] = useState(false);
  const [configInfo, setConfigInfo] = useState<GlobalConfigInfo | null>(null);

  const initializeGlobalConfig = async () => {
    if (!program || !publicKey) {
      alert('Please connect wallet');
      return;
    }

    setLoading(true);
    try {

      const tx = await program.methods
        .initializeGlobalConfig()
        .accounts({})
        .rpc();

      alert(`Global config initialized successfully! Transaction: ${tx}`);
      await fetchGlobalConfig();
    } catch (error) {
      console.error('Error initializing global config:', error);
      alert(`Error initializing global config: ${error}`);
    }
    setLoading(false);
  };

  const fetchGlobalConfig = async () => {
    if (!program) return;

    setLoading(true);
    try {
      const [globalConfig] = PublicKey.findProgramAddressSync(
        [Buffer.from('global_config')],
        program.programId
      );

      const configAccount = await program.account.globalConfig.fetch(globalConfig);

      setConfigInfo({
        admins: configAccount.admins.map((admin: any) => admin.toString()).filter((admin: string) => admin !== PublicKey.default.toString()),
        adminCount: configAccount.adminCount,
        isInitialized: true,
      });
    } catch (error) {
      console.log('Global config not initialized yet');
      setConfigInfo({
        admins: [],
        adminCount: 0,
        isInitialized: false,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGlobalConfig();
  }, [program]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-habitat-neutral-900">Global Configuration</h2>

      {!configInfo?.isInitialized ? (
        <div className="card">
          <h3 className="text-xl font-bold text-habitat-primary mb-4">Initialize Global Config</h3>
          <p className="text-sm text-habitat-neutral-600 mb-4">
            The global configuration has not been initialized yet. Click the button below to initialize it.
          </p>
          <p className="text-sm text-habitat-neutral-600 mb-6">
            <strong>Note:</strong> The connected wallet will become the super admin.
          </p>

          <button
            onClick={initializeGlobalConfig}
            disabled={loading}
            className={`btn-primary ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Initializing...' : 'Initialize Global Config'}
          </button>
        </div>
      ) : (
        <>
          <div className="card">
            <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Current Configuration</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-habitat-neutral-500 mb-1">Total Admins</p>
                <p className="text-3xl font-bold text-habitat-primary">
                  {configInfo.adminCount} / 10
                </p>
                <p className="text-xs text-habitat-neutral-400 mt-1">slots used</p>
              </div>
              <div>
                <p className="text-sm text-habitat-neutral-500 mb-1">Active Admins</p>
                <p className="text-3xl font-bold text-habitat-secondary">
                  {configInfo.admins.length}
                </p>
                <p className="text-xs text-habitat-neutral-400 mt-1">configured</p>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Admin List</h3>
            {configInfo.admins.length > 0 ? (
              <div className="space-y-3">
                {configInfo.admins.map((admin, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-habitat-neutral-50 rounded-habitat border border-habitat-neutral-200"
                  >
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-habitat-primary text-white text-sm font-semibold rounded">
                        #{index + 1}
                      </span>
                      <span className="font-mono text-sm text-habitat-neutral-900 break-all">
                        {admin}
                      </span>
                    </div>
                    {index === 0 && (
                      <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-semibold rounded-full">
                        Super Admin
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-habitat-neutral-600 text-center py-4">No admins found</p>
            )}
          </div>
        </>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-habitat-neutral-900">Actions</h3>
          <button
            onClick={fetchGlobalConfig}
            disabled={loading}
            className={`btn-outline ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Refreshing...' : 'Refresh Config'}
          </button>
        </div>
      </div>

      <div className="card bg-habitat-neutral-50">
        <h3 className="font-semibold text-habitat-neutral-900 mb-3">Configuration Info</h3>
        <ul className="space-y-2 text-sm text-habitat-neutral-600">
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Global config must be initialized before using staking features</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>The first admin has super admin privileges and cannot be removed</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Only the super admin can add/remove other admins</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Maximum 10 admins can be configured</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span className="break-all">Program ID: {program?.programId.toString()}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default GlobalConfig;
